import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { noop } from 'lodash';

import { SaveStatus } from '../../shared/SavePanel';
import { TagMedia } from '../TagMedia/TagMedia';
import { TagRelationsInput } from '../TagRelationsInput/TagRelationsInput';
import { LabelInput, NewRow, SavePanel, Section } from '../../shared';
import { LanguageType } from '@lifeserver/shared';

import { useProcessedMediaStatuses } from '../../hooks/medias';

import { ImageFileUploading } from '../../utils/upload';
import { languages } from '../../utils/utils';
import { mediaTypeToeExpectedDimensions } from '../../utils/validators';

import { MediaType } from '../../types/enums/mediatype.enum';

import { Media } from '../../types/mediaTypes';
import { CreateOrUpdateTag, Tag } from '../../types/tagTypes';

import { createEmptyTag } from '../emptyTag';
import validate from '../validate';
import styles from './TagDetails.module.scss';

export enum PossibleTagFields {
    StringId = 'stringId',
    Name = 'name',
    Synonyms = 'synonyms',
    Parents = 'parents',
    Children = 'children',
}

type MediaOrImageFileUploading = Media | (ImageFileUploading & { id?: number });

type TagDetailsProps = {
    language: LanguageType;
    isNew: boolean;
    initialTag: Tag | CreateOrUpdateTag;
    onSave: (tag: CreateOrUpdateTag) => Promise<boolean>;
    onDelete?: (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
    isSaving: boolean;
};

export const TagDetails = (props: TagDetailsProps) => {
    const {
        language,
        isNew,
        initialTag,
        isSaving,
        onSave,
        onDelete = () => Promise.resolve(),
    } = props;
    const [tag, setTag] = useState<Tag | CreateOrUpdateTag>(initialTag);
    const [iconMedia, setIconMedia] = useState<
        MediaOrImageFileUploading | undefined
    >(
        'iconMedia' in initialTag
            ? (initialTag.iconMedia ?? undefined)
            : undefined,
    );
    const [isSavedStatusShown, setIsSavedStatusShown] = useState(false);
    const {
        getStatus: getProcessedStatus,
        onProcessingComplete,
        onProcessingError,
    } = useProcessedMediaStatuses();

    useEffect(() => {
        setTag(initialTag);
        setIconMedia(
            'iconMedia' in initialTag
                ? (initialTag.iconMedia ?? undefined)
                : undefined,
        );
    }, [initialTag]);

    const hasUnsavedChanges = useMemo(() => {
        const existingIconMedia =
            'iconMedia' in initialTag
                ? (initialTag.iconMedia ?? undefined)
                : undefined;

        return (
            !_.isEqual(tag, initialTag) ||
            !_.isEqual(existingIconMedia, iconMedia)
        );
    }, [tag, initialTag, iconMedia]);

    const setHasUnsavedChanges = (value: boolean) => {
        localStorage.setItem('hasUnsavedChanges', String(value));
    };

    useEffect(() => {
        setHasUnsavedChanges(hasUnsavedChanges);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        return () => {
            setHasUnsavedChanges(false);
        };
    }, []);

    useEffect(() => {
        if (isSavedStatusShown) {
            setTimeout(() => {
                setIsSavedStatusShown(false);
            }, 2000);
        }
    }, [isSavedStatusShown]);

    const parseSynonyms = (input: string): string[] =>
        input.split(',').map((s) => s.trim());

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name as PossibleTagFields;
        const value = e.target.value;

        if (name === PossibleTagFields.Synonyms) {
            setTag((currentTag) => {
                const nextTag = _.cloneDeep(currentTag);
                nextTag.synonyms = parseSynonyms(value);
                return nextTag;
            });
            return;
        }

        setTag((currentTag) => {
            const nextTag = _.cloneDeep(currentTag);

            if (name === PossibleTagFields.Name) {
                nextTag.name = nextTag.name || createEmptyTag().name;
                nextTag.name[language] = value;
            } else if (name === PossibleTagFields.StringId) {
                nextTag.stringId = value.trim();
            }

            return nextTag;
        });
    };

    const onRelationsChange =
        (field: PossibleTagFields.Parents | PossibleTagFields.Children) =>
        (tagIds: number[]) => {
            setTag((currentTag) => {
                const nextTag = _.cloneDeep(currentTag);

                if (field === PossibleTagFields.Parents) {
                    nextTag.parents = tagIds;
                    nextTag.children = [];
                } else {
                    nextTag.children = tagIds;
                    nextTag.parents = [];
                }

                return nextTag;
            });
        };

    const onIconSelected = (_type: MediaType, files: ImageFileUploading[]) => {
        if (!files.length) {
            return;
        }

        setIconMedia(files[0]);
    };

    const onIconUploaded = (
        _type: MediaType,
        _file: File,
        res: AxiosResponse<{ uploaded: Media }>,
    ) => {
        setIconMedia(
            _.assign(res.data.uploaded as Media, {
                uploading: false,
            }),
        );
    };

    const getSaveStatus = (): SaveStatus => {
        if (isSaving) {
            return SaveStatus.SAVING;
        }

        if (hasUnsavedChanges) {
            return SaveStatus.HAS_CHANGED;
        }

        return SaveStatus.NO_CHANGES;
    };

    const getTagCreateUpdateBody = (): CreateOrUpdateTag => {
        const base = _.omit(tag, ['id', 'iconMedia']) as Omit<
            CreateOrUpdateTag,
            'iconMediaId'
        >;
        return {
            ...base,
            iconMediaId: iconMedia?.id ?? null,
        };
    };

    const save = async () => {
        const isSaved = await onSave(getTagCreateUpdateBody());

        if (isSaved) {
            setHasUnsavedChanges(false);
            setIsSavedStatusShown(true);
        }
    };

    const iconMediaItems = [
        iconMedia,
        'iconMedia' in initialTag ? initialTag.iconMedia : undefined,
    ]
        .filter((m): m is MediaOrImageFileUploading => !!m)
        .slice(0, 1);

    const iconDimensions =
        mediaTypeToeExpectedDimensions[MediaType.TAG_ICON] || {};
    const { w = 54, h = 54 } = Array.isArray(iconDimensions)
        ? iconDimensions[0]
        : iconDimensions;

    const validationResults = validate(getTagCreateUpdateBody(), {
        iconMediaStatus: getProcessedStatus(iconMediaItems[0]),
    });

    return (
        <>
            <Section
                title={
                    isNew
                        ? `New Tag: ${tag.stringId || '(unsaved)'}`
                        : tag.stringId
                }
            >
                <div className={styles.stringIdRow}>
                    <LabelInput
                        size="semiwide"
                        name={PossibleTagFields.StringId}
                        value={tag.stringId || ''}
                        tooltip="String ID in the localization sheet"
                        label="String ID"
                        onChange={onInputChange}
                    />
                    <button
                        type="button"
                        disabled={!tag.stringId}
                        onClick={() => undefined}
                    >
                        Load from Google Sheet
                    </button>
                </div>
                <NewRow />
                <LabelInput
                    size="semiwide"
                    name={PossibleTagFields.Name}
                    value={tag.name[language] || ''}
                    tooltip=""
                    label="Tag name in "
                    labelHighlight={languages[language]}
                    onChange={onInputChange}
                />
                <NewRow />
                <LabelInput
                    size="semiwide"
                    name={PossibleTagFields.Synonyms}
                    value={tag.synonyms.join(', ')}
                    tooltip="Comma-separated list of synonyms. Only letters and dashes are allowed."
                    label="Synonyms"
                    onChange={onInputChange}
                />
                <NewRow />
            </Section>
            <Section title="Tag icon">
                <TagMedia
                    type={MediaType.TAG_ICON}
                    header={`Tag icon (${w} x ${h}, PNG)`}
                    filename={tag.stringId}
                    items={iconMediaItems}
                    onMediaSelect={onIconSelected}
                    onMediaWasUploaded={onIconUploaded}
                    onMediaDragEnd={noop}
                    buttonLabel={iconMediaItems.length ? 'Replace' : 'Add new'}
                    imageSize="@3x"
                    onProcessingComplete={onProcessingComplete}
                    onProcessingError={onProcessingError}
                />
            </Section>
            <Section title="Relations">
                <TagRelationsInput
                    label="Parents"
                    value={tag.parents}
                    onChange={onRelationsChange(PossibleTagFields.Parents)}
                    excludedTagIds={'id' in tag ? [tag.id] : []}
                    disabled={tag.children.length > 0}
                />
                <div className={styles.orDivider}>OR</div>
                <TagRelationsInput
                    label="Children"
                    value={tag.children}
                    onChange={onRelationsChange(PossibleTagFields.Children)}
                    excludedTagIds={'id' in tag ? [tag.id] : []}
                    disabled={tag.parents.length > 0}
                />
            </Section>
            <SavePanel
                title={`Working with tag: ${tag.stringId || '(unsaved)'}`}
                validationResults={validationResults}
                item={tag}
                save={save}
                showDelete={!isNew}
                saveStatus={
                    isSavedStatusShown ? SaveStatus.SAVED : getSaveStatus()
                }
                onDelete={onDelete}
            />
        </>
    );
};
